import React, { useState } from 'react';
import {
    Row,
    Col,
    Card,
    Tag,
    Spin,
    Typography,
    Space,
    Divider,
    Drawer,
    Descriptions,
    Badge,
    Timeline,
    Empty,
    Button,
    Alert,
    Tooltip,
} from 'antd';
import {
    ArrowLeftOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    WifiOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    PictureOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { getPatrolDetail } from '../../../config/services/patrolService';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

// ── Status helpers ───────────────────────────────────────────
const SCAN_STATUS_CONFIG = {
    on_time: { color: 'green', icon: <CheckCircleOutlined />, label: 'On Time' },
    late: { color: 'orange', icon: <ClockCircleOutlined />, label: 'Late' },
    location_unverified: { color: 'gold', icon: <EnvironmentOutlined />, label: 'Location Unverified' },
    invalid_transition: { color: 'red', icon: <CloseCircleOutlined />, label: 'Invalid Transition' },
};

const PATROL_STATUS_COLORS = {
    active: 'blue',
    completed: 'green',
    cancelled: 'red',
    paused: 'orange',
};

const formatSeconds = (s) => {
    if (!s && s !== 0) return '—';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ── Scan Detail Drawer ───────────────────────────────────────
const ScanDetailDrawer = ({ scan, visible, onClose }) => {
    if (!scan) return null;
    const statusCfg = SCAN_STATUS_CONFIG[scan.status] ?? { color: 'default', label: scan.status };
    const { gps } = scan;

    return (
        <Drawer
            title={
                <Space>
                    <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
                    <Text>{scan.checkpoint?.name}</Text>
                </Space>
            }
            open={visible}
            onClose={onClose}
            width={480}
            destroyOnClose
        >
            {/* Timing */}
            <Descriptions title="Timing" size="small" column={1} bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Effective Scan Time">
                    {scan.effectiveScannedAt ? dayjs(scan.effectiveScannedAt).format('DD MMM YYYY, HH:mm:ss') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Server Received">
                    {scan.serverReceivedAt ? dayjs(scan.serverReceivedAt).format('DD MMM YYYY, HH:mm:ss') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Expected">
                    {formatSeconds(scan.expectedSeconds)}
                </Descriptions.Item>
                <Descriptions.Item label="Grace">
                    {formatSeconds(scan.graceSeconds)}
                </Descriptions.Item>
                <Descriptions.Item label="Actual">
                    {formatSeconds(scan.actualSeconds)}
                </Descriptions.Item>
                <Descriptions.Item label="Delay">
                    {scan.delaySeconds > 0 ? (
                        <Text type="warning">{formatSeconds(scan.delaySeconds)} late</Text>
                    ) : '—'}
                </Descriptions.Item>
            </Descriptions>

            {/* Offline / Suspicious Time Flags */}
            {scan.wasOffline && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WifiOutlined />}
                    message="Device was offline when this scan was recorded."
                    style={{ marginBottom: 10 }}
                />
            )}
            {scan.deviceTimeSuspicious && (
                <Alert
                    type="error"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Device clock was suspicious at scan time — effective time may differ from device time."
                    description="This scan's timestamp should not be treated as verified."
                    style={{ marginBottom: 10 }}
                />
            )}

            {/* GPS */}
            {gps && (
                <Descriptions title="GPS" size="small" column={1} bordered style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Latitude">{gps.latitude}</Descriptions.Item>
                    <Descriptions.Item label="Longitude">{gps.longitude}</Descriptions.Item>
                    <Descriptions.Item label="Accuracy">
                        {gps.accuracyMeters != null ? `${gps.accuracyMeters} m` : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Distance from checkpoint">
                        {gps.distanceMeters != null ? `${gps.distanceMeters} m` : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Inside Radius">
                        {gps.insideRadius ? (
                            <Tag color="green">Yes</Tag>
                        ) : (
                            <Tag color="red">No — Outside scan radius</Tag>
                        )}
                    </Descriptions.Item>
                </Descriptions>
            )}

            {/* Guard Notes */}
            {scan.notes && (
                <>
                    <Divider orientation="left" plain style={{ fontSize: 12 }}>Guard Notes</Divider>
                    <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{scan.notes}</Text>
                </>
            )}

            {/* Instructions */}
            {scan.instructions && (
                <>
                    <Divider orientation="left" plain style={{ fontSize: 12 }}>Instructions Shown</Divider>
                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{scan.instructions}</Text>
                </>
            )}

            {/* Media */}
            {scan.media?.length > 0 && (
                <>
                    <Divider orientation="left" plain style={{ fontSize: 12 }}>Media ({scan.media.length})</Divider>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {scan.media.map((m, i) => (
                            <a key={i} href={m.url} target="_blank" rel="noreferrer">
                                <div style={{ width: 72, height: 72, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb' }}>
                                    {m.url ? (
                                        <img
                                            src={m.url}
                                            alt="Scan media"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <PictureOutlined
                                        style={{ fontSize: 24, color: '#9ca3af', display: m.url ? 'none' : 'flex' }}
                                    />
                                </div>
                            </a>
                        ))}
                    </div>
                </>
            )}

            {/* Follow-up */}
            {scan.followUp && (
                <>
                    <Divider orientation="left" plain style={{ fontSize: 12 }}>Follow-up</Divider>
                    <Tag color={scan.followUp.status === 'open' ? 'red' : scan.followUp.status === 'resolved' ? 'green' : 'orange'}>
                        {scan.followUp.status?.toUpperCase()}
                    </Tag>
                    <Text style={{ fontSize: 12, marginLeft: 6 }}>{scan.followUp.summary}</Text>
                </>
            )}
        </Drawer>
    );
};

// ── Main Component ───────────────────────────────────────────
const PatrolDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedScan, setSelectedScan] = useState(null);

    const { data: patrol, isLoading, isError } = useQuery(
        ['patrol', id],
        () => getPatrolDetail(id),
        { enabled: !!id }
    );

    if (isLoading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
    if (isError || !patrol) return (
        <Alert type="error" message="Failed to load patrol. Please try again." style={{ margin: 24 }} />
    );

    // ── Group timeline by round ──────────────────────────────
    const allRounds = {};
    (patrol.scans ?? []).forEach((s) => {
        if (!allRounds[s.roundNumber]) allRounds[s.roundNumber] = { scans: [], missed: [] };
        allRounds[s.roundNumber].scans.push({ ...s, _type: 'scan' });
    });
    (patrol.missedCheckpoints ?? []).forEach((m) => {
        if (!allRounds[m.roundNumber]) allRounds[m.roundNumber] = { scans: [], missed: [] };
        allRounds[m.roundNumber].missed.push({ ...m, _type: 'missed' });
    });
    const rounds = Object.keys(allRounds).sort((a, b) => Number(a) - Number(b));

    // ── Summary counters ─────────────────────────────────────
    const scans = patrol.scans ?? [];
    const onTime = scans.filter((s) => s.status === 'on_time').length;
    const late = scans.filter((s) => s.status === 'late').length;
    const locUnverified = scans.filter((s) => s.status === 'location_unverified').length;
    const invalidTrans = scans.filter((s) => s.status === 'invalid_transition').length;
    const missed = (patrol.missedCheckpoints ?? []).length;

    const statCards = [
        { label: 'Total Scans', value: scans.length, color: '#6366f1' },
        { label: 'On Time', value: onTime, color: '#10b981' },
        { label: 'Late', value: late, color: '#f59e0b' },
        { label: 'Location Unverified', value: locUnverified, color: '#eab308' },
        { label: 'Invalid Transitions', value: invalidTrans, color: '#ef4444' },
        { label: 'Missed', value: missed, color: '#dc2626' },
    ];

    return (
        <div>
            {/* Back */}
            <Button
                icon={<ArrowLeftOutlined />}
                type="text"
                onClick={() => navigate('/patrols')}
                style={{ marginBottom: 16 }}
            >
                Back to Patrols
            </Button>

            {/* Header card */}
            <Card style={{ marginBottom: 20, borderRadius: 12 }}>
                <Row gutter={[16, 12]}>
                    <Col xs={24} md={16}>
                        <Title level={4} style={{ marginBottom: 4 }}>
                            {patrol.property?.name ?? 'Unknown Property'}
                            <Tag
                                color={PATROL_STATUS_COLORS[patrol.status] ?? 'default'}
                                style={{ marginLeft: 12, fontSize: 12 }}
                            >
                                {patrol.status?.toUpperCase()}
                            </Tag>
                        </Title>
                        <Space wrap>
                            <Text type="secondary">Guard: <Text strong>{patrol.guard?.name}</Text></Text>
                            <Text type="secondary">Round: <Text strong>{patrol.roundNumber}</Text></Text>
                            {patrol.shiftId && <Text type="secondary">Shift: <Text strong>#{patrol.shiftId}</Text></Text>}
                        </Space>
                    </Col>
                    <Col xs={24} md={8}>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Started">
                                {patrol.startedAt ? dayjs(patrol.startedAt).format('DD MMM YYYY, HH:mm') : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Completed">
                                {patrol.completedAt ? dayjs(patrol.completedAt).format('DD MMM YYYY, HH:mm') : (
                                    <Tag color="blue">In Progress</Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>
            </Card>

            {/* Next checkpoints for active patrols */}
            {patrol.status === 'active' && patrol.nextCheckpoints?.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    message={`Next expected: ${patrol.nextCheckpoints.map((c) => c.name).join(', ')}`}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* Summary stat cards */}
            <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
                {statCards.map((s) => (
                    <Col key={s.label} xs={12} sm={8} md={4}>
                        <Card size="small" style={{ textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                            <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
                            <Text style={{ fontSize: 11, color: '#6b7280' }}>{s.label}</Text>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Timeline grouped by round */}
            {rounds.length === 0 ? (
                <Empty description="No scan data yet" />
            ) : (
                rounds.map((roundNum) => {
                    const roundData = allRounds[roundNum];
                    // Merge and sort chronologically
                    const items = [
                        ...roundData.scans.map((s) => ({
                            ...s,
                            _sortTime: s.effectiveScannedAt,
                            _type: 'scan',
                        })),
                        ...roundData.missed.map((m) => ({
                            ...m,
                            _sortTime: m.expectedAt,
                            _type: 'missed',
                        })),
                    ].sort((a, b) =>
                        dayjs(a._sortTime).valueOf() - dayjs(b._sortTime).valueOf()
                    );

                    return (
                        <Card
                            key={roundNum}
                            title={<Text strong>Round {roundNum}</Text>}
                            size="small"
                            style={{ marginBottom: 16, borderRadius: 10 }}
                        >
                            <Timeline
                                items={items.map((item) => {
                                    if (item._type === 'scan') {
                                        const cfg = SCAN_STATUS_CONFIG[item.status] ?? { color: 'gray', label: item.status };
                                        return {
                                            color: cfg.color,
                                            dot: cfg.icon,
                                            children: (
                                                <div
                                                    style={{ cursor: 'pointer', padding: '2px 0' }}
                                                    onClick={() => setSelectedScan(item)}
                                                >
                                                    <Space>
                                                        <Text strong>{item.checkpoint?.name}</Text>
                                                        <Tag color={cfg.color} style={{ fontSize: 10 }}>{cfg.label}</Tag>
                                                        {item.wasOffline && (
                                                            <Tooltip title="Scanned while offline">
                                                                <WifiOutlined style={{ color: '#9ca3af' }} />
                                                            </Tooltip>
                                                        )}
                                                        {item.deviceTimeSuspicious && (
                                                            <Tooltip title="Device clock suspicious">
                                                                <WarningOutlined style={{ color: '#ef4444' }} />
                                                            </Tooltip>
                                                        )}
                                                    </Space>
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            {item.effectiveScannedAt
                                                                ? dayjs(item.effectiveScannedAt).format('HH:mm:ss')
                                                                : '—'}
                                                        </Text>
                                                        <Text type="secondary" style={{ fontSize: 10, marginLeft: 6 }}>
                                                            — click for details
                                                        </Text>
                                                    </div>
                                                </div>
                                            ),
                                        };
                                    } else {
                                        // Missed checkpoint — always show as audit record
                                        return {
                                            color: 'red',
                                            dot: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
                                            children: (
                                                <div style={{ padding: '2px 0' }}>
                                                    <Space>
                                                        <Text strong style={{ color: '#ef4444' }}>
                                                            MISSED — {item.checkpoint?.name}
                                                        </Text>
                                                    </Space>
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            Expected: {item.expectedAt
                                                                ? dayjs(item.expectedAt).format('HH:mm:ss')
                                                                : '—'}
                                                        </Text>
                                                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                                            Reason: {item.reason?.replace(/_/g, ' ')}
                                                        </Text>
                                                    </div>
                                                </div>
                                            ),
                                        };
                                    }
                                })}
                            />
                        </Card>
                    );
                })
            )}

            {/* Scan Detail Drawer */}
            <ScanDetailDrawer
                scan={selectedScan}
                visible={!!selectedScan}
                onClose={() => setSelectedScan(null)}
            />
        </div>
    );
};

export default PatrolDetail;
